/* oxlint-disable react-hooks/exhaustive-deps -- FIXME: pre-existing errors in useCallback dependencies */
import FileSaver from 'file-saver';
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';

import {
  getFileType,
  getFileTypeIcon,
  getFileTypeIconDownload,
} from '@payaca/helpers/fileHelper';
import { JobAttachment as JobAttachmentType } from '@payaca/types/jobTypes';
import UntitledIcon from '@payaca/untitled-icons';

const imageFileExtensions = ['.jpeg', '.jpg', '.png', '.webp'];

type Props = {
  // oxlint-disable-next-line typescript/no-redundant-type-constituents -- FIXME: pre-existing error: 'any' overrides all other types in this union type.
  file: JobAttachmentType | any;
  isUpload?: boolean;
  readOnly: boolean;
  onFileChange?: (change: any, remove: boolean) => void;
  isDownloadDisabled: boolean;
};

const JobAttachment: FunctionComponent<Props> = ({
  file,
  isUpload,
  readOnly,
  onFileChange,
  isDownloadDisabled,
}: Props): JSX.Element => {
  const [editingFile, setEditingFile] = useState(null);
  const nodeRef = useRef<HTMLInputElement>(null);
  const fileTypeIcon = useMemo(
    () =>
      isUpload || isDownloadDisabled
        ? getFileTypeIcon(file)
        : getFileTypeIconDownload(file),
    [file, isDownloadDisabled, isUpload]
  );

  const fileExtension = useMemo(
    () => getFileType(file.attachmentUrl || file.termUrl),
    [file]
  );

  const isImage = useMemo(
    () => imageFileExtensions.find((t) => t === fileExtension),
    [fileExtension]
  );

  const exitEditingMode = () => {
    document.removeEventListener('mousedown', handleEditModeClick, false);
    setEditingFile(null);
  };

  const enterEditingMode = (file: any) => {
    document.addEventListener('mousedown', handleEditModeClick, false);
    setEditingFile(file);
  };

  const handleEditModeClick = (e: any) => {
    if (nodeRef.current && nodeRef.current.contains(e.target)) {
      return;
    }
    exitEditingMode();
  };

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React hook useCallback depends on onClickEdit which changes every render
  const onClickEdit = (file: any) => {
    if (editingFile === file) {
      exitEditingMode();
    } else {
      enterEditingMode(file);
    }
  };

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React hook useCallback depends on onFileNameChange which changes every render
  const onFileNameChange = (e: any, file: any) => {
    if (onFileChange) {
      const newFileName = e.target.value;
      onFileChange(
        {
          ...file,
          fileName: newFileName,
        },
        false
      );
    }
  };

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React hook useCallback depends on keyPressed which changes every render
  const keyPressed = (e: any) => {
    if (e.key === 'Enter') {
      exitEditingMode();
    }
  };

  const isEditable: boolean = useMemo(() => !!file.file, [file]); // locally uploaded file

  // locally uploaded
  const editMode: boolean = useMemo(
    () => editingFile === file.file,
    [editingFile, file]
  );

  // downloading and not readonly and not editmode
  const showDeleteIcon: boolean = useMemo(
    () => !editMode && !!isUpload && !readOnly,
    [editMode, isUpload, readOnly]
  );

  // downlaoding, not readony and editable (i.e. not uploaded yet)
  const showEditIcon: boolean = useMemo(
    () => !!isUpload && !readOnly && isEditable && !editMode,
    [isUpload, readOnly, isEditable, editMode]
  );

  // editable and in editmode
  const showInputEditInput: boolean = useMemo(
    () => isEditable && editMode,
    [isEditable, editMode]
  );

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React hook useCallback depends on onClickEdit, onFileNameChange, keyPressed which change every render
  const renderFileName = useCallback((): JSX.Element => {
    const fullFileName = isEditable
      ? file.fileName
      : `${file.fileName}${fileExtension || ''}`;
    return (
      <div className={`file-name-wrapper${showEditIcon ? ' editable' : ''}`}>
        {showInputEditInput ? (
          <input
            ref={nodeRef}
            className="file-label edit"
            type="text"
            value={file.fileName}
            onChange={(e) => onFileNameChange(e, file)}
            onKeyPress={(e) => keyPressed(e)}
          />
        ) : (
          <span>{fullFileName}</span>
        )}
        {showEditIcon && (
          <UntitledIcon
            name="edit-02.3"
            className="edit-icon"
            onClick={() => {
              onClickEdit(file.file);
            }}
          />
        )}
      </div>
    );
  }, [
    file,
    fileExtension,
    isEditable,
    keyPressed,
    onClickEdit,
    onFileNameChange,
    showEditIcon,
    showInputEditInput,
  ]);

  const renderFile = useCallback((): JSX.Element => {
    return (
      <div className="file-wrapper">
        <div className="image-icon-container">
          {isImage ? (
            <img
              src={file.attachmentUrl}
              alt={`attachment ${file.fileName}`}
            ></img>
          ) : (
            <UntitledIcon name={fileTypeIcon} className="file-type-icon" />
          )}
        </div>
        {renderFileName()}
      </div>
    );
  }, [file, fileTypeIcon, isImage, renderFileName]);

  if (isUpload) {
    // uploading files and local editable
    return (
      <div className="job-attachment">
        {showDeleteIcon && !readOnly && (
          <UntitledIcon
            name="x.3"
            className="delete-icon"
            onClick={() => {
              // oxlint-disable-next-line eslint/no-unused-expressions -- FIXME: pre-existing error: Expected expression to be used
              onFileChange && onFileChange(file, true);
            }}
          />
        )}
        {renderFile()}
      </div>
    );
  } else {
    // viewing and downloading files
    return (
      <div
        onClick={() =>
          !isDownloadDisabled ? FileSaver.saveAs(file.attachmentUrl) : null
        }
        className="job-attachment download"
      >
        <div className="background-colour" />
        {renderFile()}
      </div>
    );
  }
};

export default JobAttachment;
